import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import ReactDOM from "react-dom"
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
  ListRowProps,
} from "react-virtualized"

import { useChatInfo } from "@shared/contexts"
import { Message } from "@shared/hooks/useMessages"

interface Props {
  messages: Message[]
  messagesRef: React.RefObject<HTMLDivElement>
  renderMessage: (msg: Message, onMeasure?: () => void) => React.ReactNode
  noticeHeight: number
}

const VirtualizedMessages = memo(function VirtualizedMessages({
  messages,
  messagesRef,
  renderMessage,
  noticeHeight,
}: Props) {
  const { isChatLock } = useChatInfo()
  const listRef = useRef<List>(null)
  const [isReady, setIsReady] = useState(false)

  // 핵심 1: defaultHeight를 36으로 주면, 렌더링 전이라도 높이를 대략적으로 알아서 
  // "초기 진입 시 맨 위로 멈춰버리는 현상"을 막아줍니다.
  const cacheRef = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 36,
    }),
  )

  const setListRef = useCallback(
    (listInstance: List | null) => {
      ;(listRef as React.MutableRefObject<List | null>).current = listInstance
      if (listInstance) {
        const domNode = ReactDOM.findDOMNode(listInstance) as HTMLDivElement
        if (domNode && messagesRef) {
          if (messagesRef.current !== domNode) {
            ;(
              messagesRef as React.MutableRefObject<HTMLDivElement | null>
            ).current = domNode
          }
        }
      }
    },
    [messagesRef],
  )

  // 처음 들어올 때 타이머로만 살짝 기다렸다 보여줍니다. (무한으로 내리는 악성 로직 제거)
  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), 500)
    return () => clearTimeout(timer)
  }, [])

  const rowRenderer = useCallback(
    ({ index, key, parent, style }: ListRowProps) => {
      if (index === 0) {
        return <div key={key} style={{ ...style, height: noticeHeight }} />
      }

      const message = messages[index - 1]

      return (
        <CellMeasurer
          cache={cacheRef.current}
          columnIndex={0}
          key={key}
          parent={parent}
          rowIndex={index}
        >
          {({ measure, registerChild }) => {
            const handleImageLoad = () => {
              measure()
              // 핵심 2: 이미지 로딩 시 강제로 스크롤 끌어내리는 로직 완전 삭제
            }
            return (
              <div ref={registerChild as any} style={style}>
                {renderMessage(message, handleImageLoad)}
              </div>
            )
          }}
        </CellMeasurer>
      )
    },
    [messages, renderMessage, noticeHeight],
  )

  useEffect(() => {
    cacheRef.current.clear(0, 0)
    if (listRef.current) {
      listRef.current.recomputeRowHeights(0)
    }
  }, [noticeHeight])

  const getRowHeight = useCallback(
    ({ index }: { index: number }) => {
      if (index === 0) {
        return noticeHeight
      }
      return cacheRef.current.rowHeight({ index })
    },
    [noticeHeight],
  )

  return (
    <div
      className="virtualized-chat-container"
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        opacity: isReady ? 1 : 0,
      }}
    >
      <AutoSizer>
        {({ width, height }) => (
          <List
            ref={setListRef}
            width={width}
            height={height}
            rowCount={messages.length + 1}
            rowHeight={getRowHeight}
            rowRenderer={rowRenderer}
            deferredMeasurementCache={cacheRef.current}
            overscanRowCount={5}
            
            // 핵심 3: 이 속성이 "isChatLock이 아닐 때만 항상 최하단을 유지"하게 만들어주는 react-virtualized 자체 기능입니다.
            // 위로 스크롤해서 isChatLock이 걸리면 undefined가 되어 아무 짓도 안 합니다.
            scrollToIndex={!isChatLock && messages.length > 0 ? messages.length : undefined}
            scrollToAlignment="end"
            
            onScroll={({ scrollTop, scrollHeight, clientHeight }: any) => {
              const event = new CustomEvent("chat-scroll-sync", {
                detail: { scrollTop, scrollHeight, clientHeight },
              })
              window.dispatchEvent(event)
            }}
          />
        )}
      </AutoSizer>
    </div>
  )
})

export default VirtualizedMessages